import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, MessageCircle, Phone, Check, RefreshCw, Send, KeyRound, Sparkles, X } from "lucide-react";
import { useApi, errMsg, fileUrl, money, specLabel, fmtDate, fmtTime } from "@/lib/api";
import { useMe } from "@/context/Me";
import { Badges, Stars, StatusPill, Tape } from "@/components/bits";
import { Report } from "@/pages/Diagnose";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function Chat({ jobId, proId, myId, title }) {
  const api = useApi();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const end = useRef();
  const load = useCallback(() => api.get(`/jobs/${jobId}/chat/${proId}`).then((r) => setMsgs(r.data)).catch(() => {}), [api, jobId, proId]);
  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, [load]);
  useEffect(() => { end.current?.scrollIntoView({ block: "nearest" }); }, [msgs.length]);
  const send = async (e) => {
    e.preventDefault(); if (!text.trim()) return;
    try { await api.post(`/jobs/${jobId}/chat/${proId}`, { text }); setText(""); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="flex h-[420px] flex-col" data-testid="job-chat">
      {title && <div className="border-b border-white/10 pb-2 text-sm font-bold">{title}</div>}
      <div className="flex-1 space-y-2 overflow-y-auto py-3">
        {!msgs.length && <p className="text-center text-sm text-slate-500">Nenhuma mensagem ainda.</p>}
        {msgs.map((m) => (
          <div key={m.id} className={`flex ${m.sender_id === myId ? "justify-end" : ""}`}>
            <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.sender_id === myId ? "bg-[#FFC700] text-[#111317]" : "bg-white/10"}`}>{m.text}<div className="mt-0.5 text-[10px] opacity-60">{fmtTime(m.created_at)}</div></div>
          </div>
        ))}
        <div ref={end} />
      </div>
      <form onSubmit={send} className="flex gap-2"><input data-testid="chat-input" className="field py-2" value={text} onChange={(e) => setText(e.target.value)} placeholder="Mensagem…" /><button data-testid="chat-send" className="btn-hazard px-4 py-2"><Send size={16} /></button></form>
    </div>
  );
}

function ReviewBox({ jobId, done, onDone, who }) {
  const api = useApi();
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState("");
  if (done) return <div className="panel p-5"><div className="eyebrow mb-2">Sua avaliação</div><Stars value={done.stars} /><p className="mt-2 text-sm text-slate-300">{done.comment}</p></div>;
  const send = async () => {
    try { await api.post(`/jobs/${jobId}/review`, { stars, comment }); toast.success("Avaliação enviada!"); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="panel space-y-3 p-5" data-testid="review-box">
      <h3 className="text-2xl font-bold uppercase">Avalie {who}</h3>
      <Stars value={stars} onChange={setStars} size={32} />
      <textarea data-testid="review-comment" className="field" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Conte como foi (opcional)" />
      <button data-testid="review-submit" className="btn-hazard" disabled={!stars} onClick={send}>Enviar avaliação</button>
    </div>
  );
}

function ContactCard({ name, phone, country, label }) {
  const intl = phone ? `${country === "PT" ? "351" : "55"}${phone}` : "";
  return (
    <div className="panel p-5" data-testid="contact-card">
      <div className="eyebrow mb-1">{label}</div>
      <div className="text-xl font-bold">{name}</div>
      {phone && <div className="mt-3 flex flex-wrap gap-2">
        <a href={`tel:+${intl}`} data-testid="contact-call" className="btn-ghost px-4 py-2 text-sm"><Phone size={16} />+{intl}</a>
        <a href={`https://wa.me/${intl}`} target="_blank" rel="noreferrer" data-testid="contact-whatsapp" className="btn-hazard bg-emerald-500 px-4 py-2 text-sm text-white hover:bg-emerald-600"><MessageCircle size={16} />WhatsApp</a>
      </div>}
      <p className="mt-3 text-xs text-slate-500">O pagamento do serviço é feito diretamente ao profissional, fora do app.</p>
    </div>
  );
}

function ProposalForm({ job, onDone }) {
  const api = useApi();
  const { me, refresh } = useMe();
  const [f, setF] = useState({ price: "", deadline: "", message: "" });
  const verified = me?.verification?.status === "approved";
  const send = async (e) => {
    e.preventDefault();
    try { await api.post(`/jobs/${job.id}/proposals`, { ...f, price: parseFloat(String(f.price).replace(",", ".")) }); toast.success("Proposta enviada!"); refresh(); onDone(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  if (!verified) return <div className="panel p-5 text-sm text-amber-200" data-testid="proposal-not-verified">Sua verificação está em análise. Assim que for aprovada você poderá enviar propostas.</div>;
  return (
    <form onSubmit={send} className="panel space-y-4 p-5" data-testid="proposal-form">
      <h3 className="text-2xl font-bold uppercase">Enviar proposta</h3>
      {me?.proposals_limit != null && <p className="text-xs text-slate-400">Plano Grátis: {me.proposals_used}/{me.proposals_limit} propostas este mês. <Link to="/planos" className="font-bold text-[#FFC700]">Ilimitadas no Pro</Link></p>}
      <div className="grid gap-3 sm:grid-cols-2">
        <label><span className="label">Valor ({job.country === "PT" ? "€" : "R$"})</span><input data-testid="proposal-price" className="field" inputMode="decimal" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} required /></label>
        <label><span className="label">Prazo</span><input data-testid="proposal-deadline" className="field" value={f.deadline} onChange={(e) => setF({ ...f, deadline: e.target.value })} placeholder="Ex.: 2 dias" required /></label>
      </div>
      <label className="block"><span className="label">Mensagem</span><textarea data-testid="proposal-message" className="field" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} required /></label>
      <button data-testid="proposal-submit" className="btn-hazard w-full">Enviar proposta</button>
    </form>
  );
}

function CodeEntry({ job, onDone }) {
  const api = useApi();
  const [code, setCode] = useState("");
  const send = async () => {
    try { await api.post(`/jobs/${job.id}/complete`, { code }); toast.success("Serviço concluído!"); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="panel space-y-3 p-5" data-testid="code-entry">
      <Tape className="rounded" />
      <h3 className="flex items-center gap-2 text-2xl font-bold uppercase"><KeyRound className="text-[#FFC700]" />Concluir serviço</h3>
      <p className="text-sm text-slate-400">Peça ao cliente o código de 6 dígitos ao terminar o serviço.</p>
      <input data-testid="code-input" className="field text-center font-mono text-3xl tracking-[0.4em]" maxLength={6} inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="••••••" />
      <button data-testid="code-submit" className="btn-hazard w-full" disabled={code.length !== 6} onClick={send}><Check size={18} />Confirmar código</button>
    </div>
  );
}

export default function JobDetail() {
  const { id } = useParams();
  const api = useApi();
  const { me } = useMe();
  const [job, setJob] = useState(null);
  const [chatWith, setChatWith] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const load = useCallback(() => api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch((e) => toast.error(errMsg(e))), [api, id]);
  useEffect(() => { load(); }, [load]);
  if (!job) return <div className="p-10 text-center text-slate-500">Carregando…</div>;
  const isClient = job.client_id === me?.user_id;
  const isMyPro = job.pro_id === me?.user_id;
  const accepted = job.proposals.find((p) => p.status === "accepted");
  const myProp = !isClient && job.proposals[0];

  const accept = async (p) => {
    try { await api.post(`/proposals/${p.id}/accept`); toast.success("Proposta aceita!"); setConfirm(null); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const act = async (path, msg) => { try { await api.post(path); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
      <div className="mb-6 fade-up">
        <div className="flex flex-wrap items-center gap-2"><StatusPill s={job.status} /><span className="text-sm text-slate-400">{specLabel(job.specialty, job.country)} · {fmtDate(job.created_at)}</span></div>
        <h1 className="mt-2 text-4xl font-black uppercase sm:text-5xl" data-testid="job-title">{job.title}</h1>
        <div className="mt-1 text-sm text-slate-400"><MapPin size={14} className="mr-1 inline" />{job.address ? `${job.address} · ` : ""}{job.district}, {job.city}</div>
      </div>
      <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <div className="space-y-6">
          <div className="panel p-5">
            <p className="whitespace-pre-wrap text-slate-300">{job.description}</p>
            {job.photos?.length > 0 && <div className="mt-4 flex flex-wrap gap-3">{job.photos.map((p) => <a key={p} href={fileUrl(p)} target="_blank" rel="noreferrer"><img src={fileUrl(p)} alt="" className="h-28 w-28 rounded-xl object-cover" /></a>)}</div>}
          </div>
          {job.diagnosis && <details className="group"><summary className="mb-3 flex cursor-pointer items-center gap-2 font-bold text-[#FFC700]" data-testid="job-diagnosis-toggle"><Sparkles size={16} />Ver diagnóstico da obra</summary><Report d={job.diagnosis} country={job.country} /></details>}

          {isClient && job.status === "open" && (
            <div className="space-y-3">
              <h2 className="text-2xl font-bold uppercase">Propostas ({job.proposals.length})</h2>
              {!job.proposals.length && <p className="panel p-5 text-sm text-slate-400">Aguardando propostas de profissionais da região. Você será avisado.</p>}
              {job.proposals.map((p) => (
                <div key={p.id} className={`panel p-5 ${p.pro.is_featured ? "border-orange-500/40" : ""}`} data-testid={`proposal-${p.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <Link to={`/profissional/${p.pro_id}`} className="text-xl font-bold hover:text-[#FFC700]" data-testid={`proposal-pro-link-${p.id}`}>{p.pro.full_name}</Link>
                      <div className="mt-1 flex flex-wrap items-center gap-2"><Stars value={p.pro.rating_avg} count={p.pro.rating_count} size={14} /><Badges u={p.pro} /></div>
                      {p.distance_km != null && <div className="mt-1 text-xs text-slate-400">{p.distance_km} km de você</div>}
                    </div>
                    <div className="text-right"><div className="font-display text-3xl font-black text-[#FFC700]">{money(p.price, job.country)}</div><div className="text-xs text-slate-400">Prazo: {p.deadline}</div></div>
                  </div>
                  <p className="mt-3 text-sm text-slate-300">{p.message}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button data-testid={`proposal-chat-${p.id}`} className="btn-ghost px-4 py-2 text-sm" onClick={() => setChatWith(p)}><MessageCircle size={16} />Conversar</button>
                    <button data-testid={`proposal-accept-${p.id}`} className="btn-hazard px-4 py-2 text-sm" onClick={() => setConfirm(p)}><Check size={16} />Aceitar</button>
                  </div>
                </div>
              ))}
              <button data-testid="job-cancel" className="text-sm text-slate-500 hover:text-rose-400" onClick={() => act(`/jobs/${job.id}/cancel`, "Chamado cancelado")}>Cancelar chamado</button>
            </div>
          )}
          {(isClient || isMyPro) && job.status !== "open" && accepted && job.status !== "cancelled" && (
            <div className="panel p-5"><Chat jobId={job.id} proId={accepted.pro_id} myId={me.user_id} title={isClient ? `Conversa com ${accepted.pro.full_name}` : `Conversa com ${job.client.full_name}`} /></div>
          )}
          {!isClient && myProp && job.status === "open" && (
            <div className="panel p-5">
              <div className="mb-3 flex items-center justify-between"><div className="eyebrow">Sua proposta</div><StatusPill s={myProp.status} /></div>
              <div className="font-display text-3xl font-black text-[#FFC700]">{money(myProp.price, job.country)}</div>
              <p className="text-sm text-slate-300">{myProp.message}</p>
              <div className="mt-4 border-t border-white/10 pt-4"><Chat jobId={job.id} proId={me.user_id} myId={me.user_id} title={`Conversa com ${job.client.full_name || "cliente"}`} /></div>
            </div>
          )}
        </div>

        <div className="space-y-6">
          {isClient && job.status === "assigned" && (
            <div className="panel overflow-hidden" data-testid="confirm-code-panel">
              <Tape />
              <div className="p-6 text-center">
                <div className="eyebrow mb-2">Código de confirmação</div>
                <div data-testid="confirm-code" className="select-all rounded-2xl border-2 border-yellow-300 bg-[#FFC700] px-4 py-5 font-mono text-5xl font-extrabold tracking-[0.3em] text-[#111317] shadow-[0_10px_40px_rgba(255,199,0,0.25)]">{job.confirm_code}</div>
                <p className="mt-4 text-sm text-slate-400">Passe este código ao profissional <b>somente quando o serviço estiver pronto</b>.</p>
                {job.code_attempts > 0 && <p className="mt-2 text-xs text-amber-300">Tentativas erradas: {job.code_attempts}/5</p>}
                <button data-testid="new-code-btn" className="btn-ghost mt-4 px-4 py-2 text-sm" onClick={() => act(`/jobs/${job.id}/new-code`, "Novo código gerado")}><RefreshCw size={14} />Gerar novo código</button>
              </div>
            </div>
          )}
          {isClient && accepted && job.status !== "open" && <ContactCard label="Profissional contratado" name={accepted.pro.full_name} phone={accepted.pro.phone} country={job.country} />}
          {isClient && accepted && <div className="panel p-5 text-sm"><div className="eyebrow mb-1">Valor combinado</div><div className="font-display text-3xl font-black">{money(job.agreed_price, job.country)}</div></div>}
          {isMyPro && job.status === "assigned" && <><ContactCard label="Cliente" name={job.client.full_name} phone={job.client.phone} country={job.country} /><CodeEntry job={job} onDone={load} /></>}
          {!isClient && !myProp && job.status === "open" && <ProposalForm job={job} onDone={load} />}
          {job.status === "completed" && (isClient || isMyPro) && <ReviewBox jobId={job.id} done={job.my_review} onDone={load} who={isClient ? "o profissional" : "o cliente"} />}
          {!isClient && <div className="panel p-5 text-sm"><div className="eyebrow mb-1">Cliente</div><div className="font-bold">{job.client.full_name}</div><Stars value={job.client.rating_avg} count={job.client.rating_count || 0} size={14} /></div>}
        </div>
      </div>

      <Dialog open={!!chatWith} onOpenChange={(o) => !o && setChatWith(null)}>
        <DialogContent className="border-white/10 bg-[#181B20] sm:max-w-lg">
          <DialogHeader><DialogTitle>Conversa com {chatWith?.pro.full_name}</DialogTitle></DialogHeader>
          {chatWith && <Chat jobId={job.id} proId={chatWith.pro_id} myId={me.user_id} />}
        </DialogContent>
      </Dialog>
      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent className="border-white/10 bg-[#181B20]">
          <DialogHeader><DialogTitle>Aceitar proposta de {confirm?.pro.full_name}?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-300">Valor: <b>{confirm && money(confirm.price, job.country)}</b>. As outras propostas serão recusadas e você receberá o contato do profissional e o código de confirmação.</p>
          <div className="flex justify-end gap-2"><button className="btn-ghost px-4 py-2" onClick={() => setConfirm(null)} data-testid="accept-cancel"><X size={16} />Voltar</button><button data-testid="accept-confirm" className="btn-hazard px-4 py-2" onClick={() => accept(confirm)}><Check size={16} />Aceitar</button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
